import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const SDK_SRC_SANDBOX = "https://www.sandbox.paypal.com/web-sdk/v6/core";
const SDK_SRC_LIVE = "https://www.paypal.com/web-sdk/v6/core";

// Cache loader so we don't inject script twice
let sdkLoadPromise = null;
let loadedSrc = null;

function loadPayPalV6(src) {
  if (sdkLoadPromise && loadedSrc === src) return sdkLoadPromise;
  loadedSrc = src;
  sdkLoadPromise = new Promise((resolve, reject) => {
    if (window.paypal && window.paypal.createInstance) {
      resolve(window.paypal);
      return;
    }
    const existing = document.querySelector(`script[data-paypal-v6="true"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.dataset.paypalV6 = "true";
    s.onload = () => {
      if (window.paypal && window.paypal.createInstance) resolve(window.paypal);
      else reject(new Error("PayPal SDK loaded but unavailable"));
    };
    s.onerror = () => reject(new Error("Failed to load PayPal v6 SDK"));
    document.head.appendChild(s);
  });
  return sdkLoadPromise;
}

/**
 * PayPalCheckout — PayPal SDK v6 button using <paypal-button> web component.
 * Calls our backend to create + capture orders securely (Orders v2 API).
 */
const PayPalCheckout = ({
  amount,
  currency,
  donorName,
  donorEmail,
  message,
  donationType,
  locale,
  anonymous = false,
  campaignSlug,
  onSuccess,
}) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const sessionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        // Get config from backend
        const { data: cfg } = await api.get("/paypal/config");
        if (!cfg.configured || !cfg.client_id) {
          if (!cancelled) setError("paypal-not-configured");
          return;
        }
        const src = cfg.env === "live" || cfg.env === "production" ? SDK_SRC_LIVE : SDK_SRC_SANDBOX;
        const paypal = await loadPayPalV6(src);
        if (cancelled) return;

        const sdk = await paypal.createInstance({
          clientId: cfg.client_id,
          components: ["paypal-payments"],
          pageType: "checkout",
          locale: locale === "en" ? "en-US" : "fr-FR",
        });
        if (cancelled) return;

        // Eligibility check
        const methods = await sdk.findEligibleMethods({ currencyCode: currency });
        if (!methods.isEligible("paypal")) {
          if (!cancelled) setError("not-eligible");
          return;
        }

        const session = sdk.createPayPalOneTimePaymentSession({
          async onApprove(data) {
            try {
              const { data: cap } = await api.post(
                `/paypal/orders/${data.orderId}/capture`
              );
              if (cap?.capture?.status === "COMPLETED") {
                toast.success("Paiement reçu — merci !");
                onSuccess?.(cap);
              } else {
                toast.error("Paiement non finalisé");
              }
            } catch (e) {
              toast.error("Erreur lors de la capture du paiement");
            }
          },
          onCancel() {
            toast("Paiement annulé");
          },
          onError(err) {
            console.error("PayPal payment error", err);
            toast.error("Erreur PayPal");
          },
        });
        sessionRef.current = session;

        // Wire button
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          const btn = document.createElement("paypal-button");
          btn.setAttribute("data-testid", "paypal-v6-button");
          containerRef.current.appendChild(btn);

          btn.addEventListener("click", async () => {
            try {
              await session.start(
                { presentationMode: "auto" },
                createOrderPromise()
              );
            } catch (e) {
              console.error("PayPal start error", e);
              toast.error("Impossible de démarrer le paiement");
            }
          });
        }

        if (!cancelled) setReady(true);
      } catch (e) {
        console.error("PayPal init failed", e);
        if (!cancelled) setError("init-failed");
      }
    };

    const createOrderPromise = () =>
      api
        .post("/paypal/orders", {
          amount,
          currency,
          donation_type: donationType,
          donor_name: donorName,
          donor_email: donorEmail,
          message: message || null,
          anonymous,
          campaign_slug: campaignSlug || null,
        })
        .then(({ data }) => ({ orderId: data.order_id }));

    init();

    return () => {
      cancelled = true;
    };
    // Re-init when amount/currency/donor info change so the button reflects new order
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, currency, donorName, donorEmail, donationType, locale, anonymous, campaignSlug]);

  if (error === "paypal-not-configured") return null; // parent shows fallback
  if (error === "not-eligible") {
    return (
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
        PayPal indisponible pour cette devise dans votre région.
      </p>
    );
  }
  if (error === "init-failed") {
    return (
      <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
        Impossible d'initialiser PayPal. Réessayez dans un instant.
      </p>
    );
  }

  return (
    <div data-testid="paypal-v6-container">
      <div ref={containerRef} className="paypal-button-wrap" />
      {!ready && (
        <div className="text-center text-sm text-[#64748B] py-3">Chargement de PayPal...</div>
      )}
      <style>{`
        .paypal-button-wrap paypal-button {
          display: block;
          width: 100%;
          min-height: 50px;
        }
      `}</style>
    </div>
  );
};

export default PayPalCheckout;
